import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  ChangePasswordRequestSchema,
  HttpProblemError,
  type ChangePasswordRequest,
} from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'

interface FormValues extends ChangePasswordRequest {
  confirmPassword: string
}

export function ChangePasswordPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const forced = auth.user?.mustChangePassword === true
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(
      ChangePasswordRequestSchema.extend({
        // For a voluntary change require the current password client-side
        // before bothering the server. For a forced change there is no
        // field to fill in, so the schema falls through to optional().
        currentPassword: forced
          ? ChangePasswordRequestSchema.shape.currentPassword
          : ChangePasswordRequestSchema.shape.currentPassword.unwrap().min(1),
        // Keep confirm-password validation client-side; the backend doesn't
        // need to know about it. The mismatch check fires before submission
        // so we don't trigger an empty server round-trip.
        confirmPassword: ChangePasswordRequestSchema.shape.newPassword,
      }).refine((v) => v.newPassword === v.confirmPassword, {
        path: ['confirmPassword'],
        message: t('changePassword.mismatch'),
      }),
    ),
  })
  const newPassword = watch('newPassword')

  const onSubmit = async (values: FormValues) => {
    setServerError(null)
    try {
      await api().auth.changePassword({
        // The backend ignores currentPassword on a forced change; sending
        // `undefined` keeps the request body tidy and the intent obvious.
        currentPassword: forced ? undefined : values.currentPassword,
        newPassword: values.newPassword,
      })
      // Pull the refreshed user record so mustChangePassword flips to false
      // and the RequireAuth gate releases the rest of the app.
      await auth.refresh()
      void navigate('/', { replace: true })
    } catch (err) {
      if (err instanceof HttpProblemError) {
        setServerError(err.problem.detail ?? err.problem.title)
        return
      }
      setServerError(t('common.unexpectedError'))
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4 dark:bg-stone-950">
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e)
        }}
        className="w-full max-w-sm space-y-4 rounded-lg border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-900"
      >
        <div>
          <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t('changePassword.title')}
          </h1>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {forced ? t('changePassword.subtitleForced') : t('changePassword.subtitle')}
          </p>
        </div>
        {forced ? null : (
          <div>
            <label className="label">{t('changePassword.current')}</label>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword')}
            />
            {errors.currentPassword ? (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.currentPassword.message}
              </p>
            ) : null}
          </div>
        )}
        <div>
          <label className="label">{t('changePassword.new')}</label>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            {...register('newPassword')}
          />
          {errors.newPassword ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.newPassword.message}
            </p>
          ) : null}
        </div>
        <div>
          <label className="label">{t('changePassword.confirm')}</label>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            {...register('confirmPassword')}
          />
          {errors.confirmPassword ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.confirmPassword.message}
            </p>
          ) : null}
        </div>
        {serverError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{serverError}</p>
        ) : null}
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={isSubmitting || !newPassword}
        >
          {isSubmitting ? t('changePassword.saving') : t('changePassword.submit')}
        </button>
      </form>
    </div>
  )
}
